import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type { Currency, Page, Transaction, TransactionType } from './types'

const ROOT = '/transactions'
const KEY = ['transactions'] as const

export type TransactionFilters = {
  organizationId?: string | null
  accountId?: string | null
  categoryId?: string | null
  projectId?: string | null
  tagId?: string | null
  type?: TransactionType
  dateFrom?: string
  dateTo?: string
  search?: string
  limit?: number
}

function filtersKey(filters: TransactionFilters) {
  return {
    organizationId: filters.organizationId ?? null,
    accountId: filters.accountId ?? null,
    categoryId: filters.categoryId ?? null,
    projectId: filters.projectId ?? null,
    tagId: filters.tagId ?? null,
    type: filters.type ?? null,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
    search: filters.search ?? null,
    limit: filters.limit ?? 50,
  }
}

export function useTransactions(
  filters: TransactionFilters = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: [...KEY, filtersKey(filters)],
    enabled: options.enabled ?? true,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api<Page<Transaction>>(
        `${ROOT}${buildQuery({
          organization_id: filters.organizationId ?? undefined,
          account_id: filters.accountId ?? undefined,
          category_id: filters.categoryId ?? undefined,
          project_id: filters.projectId ?? undefined,
          tag_id: filters.tagId ?? undefined,
          type: filters.type,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          search: filters.search,
          limit: filters.limit ?? 50,
          cursor: pageParam,
        })}`,
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}

// Fetch one transaction by id (e.g. when opening the edit form from a view that
// only has the row id). Disabled while id is null.
export function useTransaction(id: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: !!id,
    queryFn: () => api<Transaction>(`${ROOT}/${id}`),
  })
}

export function flattenTransactions(
  data: InfiniteData<Page<Transaction>> | undefined,
): Transaction[] {
  if (!data) return []
  return data.pages.flatMap((p) => p.items)
}

export type CreateTransactionInput = {
  organizationId: string
  type: TransactionType
  date: string
  accountId: string
  toAccountId?: string | null
  counterpartyId?: string | null
  categoryId: string
  projectId?: string | null
  amount: number
  currency: Currency
  toAmount?: number | null
  fxRate?: number | null
  description?: string | null
  tagIds?: string[]
}

export function useCreateTransaction() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTransactionInput) =>
      api<Transaction>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['account-balance'] })
      qc.invalidateQueries({ queryKey: ['reports', 'cashflow'] })
      qc.invalidateQueries({ queryKey: ['account-movement'] })
    },
  })
}

// The transaction type stays fixed after creation (changing it would invalidate
// the category/transfer wiring); everything else, including the booking
// organization, is editable.
export type UpdateTransactionInput = {
  id: string
  // salaryEntryId is edit-only (not a create field): it (re)links/creates the
  // salary_payments row for a salary-category expense, or unlinks it when null.
  patch: Partial<Omit<CreateTransactionInput, 'type'>> & { salaryEntryId?: string | null }
}

export function useUpdateTransaction() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateTransactionInput) =>
      api<Transaction>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['account-balance'] })
      qc.invalidateQueries({ queryKey: ['reports', 'cashflow'] })
      qc.invalidateQueries({ queryKey: ['account-movement'] })
      // A salary payout's amount/date/accrual can change here, which moves the
      // salary ledger — refresh accruals + the salary report too.
      qc.invalidateQueries({ queryKey: ['salary-entries'] })
      qc.invalidateQueries({ queryKey: ['reports', 'salaries'] })
    },
  })
}

export function useDeleteTransaction() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['account-balance'] })
      qc.invalidateQueries({ queryKey: ['reports', 'cashflow'] })
      qc.invalidateQueries({ queryKey: ['account-movement'] })
    },
  })
}
